/**
 * Геометрия и стиль слайдов — снято с макета Figma «Презентация bee16»
 * (файл em3eJeTbDBl5VeMpih79dU): титул 0:1044, пост 0:1011, статья 0:768,
 * заключение 0:6. Единица — пиксель макета; страница PDF 1920×1080 pt, как
 * при экспорте из самой Figma, поэтому 1 px макета = 1 pt.
 *
 * Числа не округлены намеренно: это координаты слоёв из макета.
 */

export const PAGE = { width: 1920, height: 1080 } as const;

export const COLORS = {
  yellow: '#FED001',
  cream: '#FFFBEA',
  ink: '#3C3A1F',
  /** Цвет заголовка титула — в макете он чуть другой, чем у остального текста. */
  coverInk: '#3F3932',
  white: '#FFFFFF',
  /** Экран телефона без превью. */
  placeholder: '#EFE9D2',
} as const;

export type FontKey =
  | 'rubik'
  | 'rubikSemiBold'
  | 'golos'
  | 'golosSemiBold'
  | 'oswaldBold'
  | 'bricolageSemiBold'
  | 'sf';

/**
 * Вертикальные метрики шрифтов (hhea = OS/2 typo, USE_TYPO_METRICS), в долях
 * кегля. Нужны, чтобы ставить базовую линию так же, как браузер и Figma:
 * строка высотой lineHeight, текст по центру строки.
 */
export const FONT_METRICS: Record<FontKey, { ascent: number; descent: number }> = {
  rubik: { ascent: 0.935, descent: 0.25 },
  rubikSemiBold: { ascent: 0.935, descent: 0.25 },
  golos: { ascent: 0.98, descent: 0.22 },
  golosSemiBold: { ascent: 0.98, descent: 0.22 },
  oswaldBold: { ascent: 1.193, descent: 0.289 },
  bricolageSemiBold: { ascent: 0.93, descent: 0.27 },
  sf: { ascent: 0.967, descent: 0.211 },
};

/** Скруглённая карточка поверх жёлтого фона — общая для слайдов, кроме титула. */
export const CARD = { x: 48, y: 44, w: 1823, h: 993, r: 43 } as const;

/** Логотип клиента в углу слайда; в макете это лого bee16 119×56. */
export type LogoAnchor = 'left' | 'center' | 'right';

export const COVER = {
  logo: { x: 159, y: 169, w: 302, h: 146, anchor: 'left' as LogoAnchor },
  title: {
    text: 'Отчет',
    x: 140,
    top: 659.4,
    size: 149.333,
    lineHeight: 194.203,
    tracking: -4.48,
  },
  pill: {
    y: 853.424,
    firstX: 145,
    gap: 26.572,
    w: 257.214,
    h: 95.372,
    border: 4.278,
    /** Центр текста по вертикали внутри плашки. */
    textCenterY: 46.958,
    size: 45.759,
    lineHeight: 95.331,
    tracking: -1.3728,
    minPadding: 28,
  },
  screen: { x: 1168.429, y: 175.053, w: 530.755, h: 1131.03, r: 72 },
  phone: { x: 1147, y: 157, w: 573, h: 1173 },
} as const;

/**
 * Слайд публикации — вариант макета со скринами статистики (0:916): слева
 * телефон с превью и плашкой @аккаунта, рядом бейдж и цифры, справа телефон
 * с экраном статистики из приложения.
 */
export const POST = {
  logo: { x: 1703, y: 100, w: 119.033, h: 55.642, anchor: 'right' as LogoAnchor },
  phone: { x: 98.72, y: 270, w: 284.221, h: 581 },
  screen: { x: 109.841, y: 279.915, w: 262.408, h: 559.188, r: 39.86 },
  /** Тёмная размытая подложка под плашкой; PNG с полями pad на размытие. */
  tagShadow: { x: 101.949, y: 727.118, w: 270.436, h: 112.009, pad: 60 },
  tag: {
    x: 110.022,
    y: 771.418,
    w: 263.373,
    h: 72.655,
    border: 3.215,
    fillOpacity: 0.2,
    borderOpacity: 0.55,
    size: 21.431,
    minSize: 14,
    tracking: -0.6429,
    lineHeight: 71.633,
    /** Центр текста от левого верхнего угла плашки. */
    textCenterX: 131.95,
    textCenterY: 36.0,
    padX: 18,
  },
  badge: { x: 400.424, y: 411, h: 56, padX: 20, textTop: 11, size: 28.619, stroke: 2, fillOpacity: 0.31 },
  stats: {
    x: 407,
    firstTop: 485.506,
    step: 94,
    lineHeight: 79,
    textX: 417,
    labelSize: 20,
    valueSize: 48,
    valueTop: 31,
  },
  button: {
    x: 131,
    y: 875,
    w: 235.498,
    h: 61.345,
    textX: 30.249,
    textTop: 12.172,
    size: 31.642,
    tracking: -0.9493,
  },
  hand: { x: 320, y: 907.06, w: 77.032, h: 80.218 },
  /**
   * Второй телефон — крупный экран статистики из приложения. Он больше
   * макетного (1,35), иначе мелкий текст экрана не читается, и стоит по
   * центру карточки: 959,5 по горизонтали, 540,5 по вертикали.
   */
  insightsPhone: {
    screen: { x: 782.65, y: 161.7, w: 354.3, h: 754.9, r: 53.8 },
    phone: { x: 767.65, y: 148.3, w: 383.7, h: 784.4 },
  },
} as const;

/**
 * Экран статистики в телефоне — это сам скриншот Instagram (iPhone, ширина
 * 1290 → 923 px), в котором стёрты превью и цифры: их рисуем своими,
 * настоящими. Шрифт, иконки и подписи — из приложения, как есть.
 * Координаты ниже — пиксели этого скриншота (см. assets/images/insights).
 */
export const SCREEN = {
  width: 923,
  colors: {
    background: '#0B1014',
    text: '#FFFFFF',
    /** Серый подписей в плитках — снят пипеткой со скриншота. */
    label: '#B3BCC8',
    /** Чёрные поля вокруг ролика в превью — как в приложении. */
    letterbox: '#000000',
  },
  /** Ряд счётчиков под превью: ♥, комментарии, репосты, отправки, сохранения. */
  counters: { size: 29.3, centers: [133, 297.5, 461, 624.5, 788.5] },
  tiles: {
    /** Левый край плиток; вторая — через tiles.x[1]. */
    x: [34, 474],
    w: 415,
    padX: 36,
    labelSize: 25,
    valueSize: 43.5,
  },
  reel: {
    /** Высота шаблона: ниже чипов идёт график, он обрезан. */
    height: 1853,
    thumb: { x: 324, y: 277, w: 275, h: 481, r: 16, letterbox: true },
    countersBaseline: 902,
    labelBaseline: [1252, 1449],
    valueBaseline: [1320.5, 1517.5],
  },
  post: {
    height: 1675,
    thumb: { x: 315, y: 275, w: 292, h: 363, r: 5, letterbox: false },
    countersBaseline: 781.5,
    labelBaseline: [1122, 1319],
    valueBaseline: [1190.5, 1387.5],
  },
} as const;

export const ARTICLE = {
  logo: { x: 1701, y: 924, w: 119.033, h: 55.642, anchor: 'right' as LogoAnchor },
  laptop: { x: 58, y: 193, w: 1456, h: 791 },
  screen: { x: 191, y: 219, w: 1190.285, h: 680.137, r: 18 },
  shot: { x: 193, y: 219, w: 1186, h: 667 },
  /** Правый край колонки плашек. */
  pillsRight: 1820,
  pill: { h: 56, padX: 20, textTop: 11, size: 28.619, stroke: 2 },
  badgeY: 100,
  domainY: 181,
  button: {
    y: 262,
    w: 207.359,
    h: 54.436,
    outerR: 23.364,
    padX: 21.436,
    textTop: 10.718,
    size: 27.861,
    stroke: 2,
    right: 1818.359,
  },
  hand: { x: 1758, y: 295, w: 67.827, h: 70.633 },
} as const;

/**
 * Слайд «Итого» перед статьями. В макете его нет — собран из деталей
 * заключения (карточка с завитком, лого и заголовок по центру) и плашек
 * слайда статьи (жёлтая заливка, тёмная обводка).
 */
export const SUMMARY = {
  logo: { x: 900, y: 300, w: 119.033, h: 55.642, anchor: 'center' as LogoAnchor },
  title: { text: 'ИТОГО', top: 395 },
  cards: {
    y: 550,
    h: 230,
    minW: 440,
    padX: 48,
    gap: 40,
    /** Отступ ряда плашек от краёв карточки. */
    sideMargin: 80,
    r: 32,
    stroke: 2,
    labelTop: 44,
    labelSize: 34,
    valueTop: 104,
    valueSize: 88,
  },
} as const;

export const OUTRO = {
  logo: { x: 900, y: 315, w: 119.033, h: 55.642, anchor: 'center' as LogoAnchor },
  title: {
    text: 'МЫ ВСЕГДА НА СВЯЗИ!',
    centerX: 960,
    top: 410,
    size: 77.5,
    lineHeight: 95.325,
    tracking: -1.55,
  },
  namePill: { centerX: 960, y: 530, minW: 294, h: 66, r: 26, padX: 42.4 },
  name: { top: 551 },
  text: { size: 26, lineHeight: 23, tracking: -1.04 },
  /**
   * Строки под именем: должность, почта, телефон, Telegram. В макете шаг
   * после строки без иконки — 47, после строки с иконкой — 54; пустые поля
   * пропускаются, остальные сдвигаются вверх с теми же шагами.
   */
  rows: { firstTop: 632, stepAfterText: 47, stepAfterIcon: 54 },
  centerX: { role: 960.5, email: 960.5, phone: 960, telegram: 960 },
  /**
   * Иконки висят слева от текста; зазор до текста у каждой свой — так их
   * расставил дизайнер. dy — сдвиг иконки от верха строки.
   */
  icons: {
    mail: { w: 26.6182, h: 21.3818, gap: 15.28, dy: 2.691 },
    phone: { w: 26.6182, h: 26.6182, gap: 6.98, dy: -2.309 },
    telegram: { w: 29, h: 29, gap: 9.45, dy: 0 },
  },
} as const;
